/**
 * DataPointContextMenu — right-click menu for any data point in any chart.
 *
 * Provides five provenance actions:
 *   1. Show full lineage           → select node, open DAG panel
 *   2. Show solver settings        → select node, open detail panel
 *   3. Show mesh                   → select geometry/mesh ancestor, open DAG
 *   4. Show flowfield              → select node, open viewer panel
 *   5. View reference constants    → select node, open config panel
 *
 * Usage: call `useDataPointContextMenu()` in any renderer, attach
 * `setHoveredPoint` / `clearHoveredPoint` to hover events, wrap the
 * chart with the returned `containerProps`, and render `<ContextMenu />`.
 */

import { useCallback, useMemo, useRef, useState, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useTheme } from "../../context/ThemeContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PointIdentity {
  nodeId: string;
  row: Record<string, unknown>;
}

interface PointMenuPosition {
  x: number;
  y: number;
  kind: "point";
  point: PointIdentity;
}

interface PlotMenuPosition {
  x: number;
  y: number;
  kind: "plot";
  chartData: Record<string, unknown>[];
}

type MenuPosition = PointMenuPosition | PlotMenuPosition;

// ---------------------------------------------------------------------------
// Semantic field lookup — maps natural language to row keys
// ---------------------------------------------------------------------------

// Each entry: row key → array of natural language phrases that should match it.
// Order matters for priority: earlier entries win on ambiguous queries.
const FIELD_ALIASES: Record<string, string[]> = {

  // ── Flow conditions ──────────────────────────────────────────────────
  Mach:            ["mach", "mach number", "freestream mach", "flight mach", "m number", "mach no"],
  mach_number:     ["mach number"],
  alpha:           ["alpha", "angle of attack", "aoa", "incidence", "incidence angle", "pitch angle", "attack angle"],
  beta:            ["beta", "sideslip", "sideslip angle", "yaw angle", "slip angle"],
  Re:              ["re", "reynolds", "reynolds number", "reynolds no"],
  reynolds_number: ["reynolds number"],
  temperature:     ["temperature", "temp", "freestream temperature", "t inf", "static temperature"],
  pressure:        ["pressure", "freestream pressure", "p inf", "static pressure"],

  // ── Force coefficients ───────────────────────────────────────────────
  CL:  ["cl", "lift coefficient", "lift coeff", "coefficient of lift", "c sub l", "c_l", "lift"],
  CD:  ["cd", "drag coefficient", "drag coeff", "coefficient of drag", "c sub d", "c_d", "drag"],
  CX:  ["cx", "axial force coefficient", "c sub x", "c_x", "axial force", "x force"],
  CY:  ["cy", "side force coefficient", "c sub y", "c_y", "side force", "lateral force", "y force"],
  CZ:  ["cz", "normal force coefficient", "c sub z", "c_z", "normal force", "z force"],
  CFx: ["cfx", "force x", "cf sub x", "cf_x", "x component force"],
  CFy: ["cfy", "force y", "cf sub y", "cf_y", "y component force"],
  CFz: ["cfz", "force z", "cf sub z", "cf_z", "z component force"],

  // ── Moment coefficients ──────────────────────────────────────────────
  CM_pitching: ["cm", "pitching moment", "cm pitching", "pitch moment", "moment coefficient",
                "nose up moment", "nose down moment", "longitudinal moment", "cm_pitching"],
  CM_rolling:  ["rolling moment", "cm rolling", "roll moment", "bank moment", "cm_rolling"],
  CM_yawing:   ["yawing moment", "cm yawing", "yaw moment", "directional moment", "cm_yawing"],

  // ── Flow360-original coefficients (before ref-qty correction) ───────
  CL_f360:          ["cl f360", "original cl", "flow360 cl", "uncorrected cl", "cl before correction"],
  CD_f360:          ["cd f360", "original cd", "flow360 cd", "uncorrected cd", "cd before correction"],
  CX_f360:          ["cx f360", "original cx", "flow360 cx"],
  CY_f360:          ["cy f360", "original cy", "flow360 cy"],
  CZ_f360:          ["cz f360", "original cz", "flow360 cz"],
  CFx_f360:         ["cfx f360", "original cfx", "flow360 cfx"],
  CFy_f360:         ["cfy f360", "original cfy", "flow360 cfy"],
  CFz_f360:         ["cfz f360", "original cfz", "flow360 cfz"],
  CM_pitching_f360: ["cm pitching f360", "original cm", "flow360 cm", "uncorrected pitching moment"],
  CM_rolling_f360:  ["cm rolling f360", "original rolling moment", "flow360 rolling moment"],
  CM_yawing_f360:   ["cm yawing f360", "original yawing moment", "flow360 yawing moment"],

  // ── Reference quantities ─────────────────────────────────────────────
  wing_area:     ["wing area", "ref area", "reference area", "sref", "s ref", "planform area",
                  "wetted area", "wing planform"],
  ref_chord:     ["chord", "reference chord", "cref", "c ref", "mac", "mean aerodynamic chord",
                  "chord length"],
  wing_span:     ["span", "wing span", "wingspan", "bref", "b ref", "reference span"],
  ref_area_f360: ["ref area f360", "flow360 reference area", "flow360 ref area",
                  "original ref area", "solver ref area"],
  ref_chord_f360:["ref chord f360", "flow360 reference chord", "flow360 ref chord",
                  "original ref chord", "solver ref chord"],

  // ── Convergence / solver ─────────────────────────────────────────────
  residual:       ["residual", "final residual", "convergence residual", "rms residual"],
  max_residual:   ["max residual", "maximum residual", "worst residual", "peak residual"],
  iterations:     ["iterations", "num iterations", "number of iterations", "iters",
                   "iteration count", "solver iterations", "steps", "time steps"],
  num_iterations: ["num iterations", "number of iterations"],

  // ── Run identity & status ────────────────────────────────────────────
  name:           ["name", "run name", "case name", "simulation name", "label"],
  status:         ["status", "run status", "job status", "state", "completion status",
                   "converged", "completed", "failed"],
  sweep_group:    ["sweep", "sweep group", "sweep id", "parametric sweep", "parameter sweep",
                   "sweep name"],
  flow360_case_id:["case id", "flow360 case", "flow360 case id", "f360 case", "solver case id",
                   "case identifier", "job id"],
  base_case_id:   ["base case", "base case id", "parent case", "forked from", "original case"],

  // ── Geometry ─────────────────────────────────────────────────────────
  geom_name:      ["geometry", "geom", "geometry name", "geom name", "cad", "cad model",
                   "shape", "body", "aircraft", "vehicle", "configuration shape"],

  // ── Mesh (ancestor-merged) ───────────────────────────────────────────
  surf_mesh_name: ["surface mesh", "surf mesh", "surface mesh name", "surface grid",
                   "skin mesh", "panel mesh"],
  vol_mesh_name:  ["volume mesh", "vol mesh", "volume mesh name", "volume grid",
                   "3d mesh", "computational mesh", "cfd mesh", "grid"],

  // ── Flow360 ancestor IDs ─────────────────────────────────────────────
  geom_flow360_geometry_id:         ["flow360 geometry id", "f360 geometry id", "geometry upload id"],
  surf_mesh_flow360_surface_mesh_id:["flow360 surface mesh id", "f360 surface mesh id"],
  vol_mesh_flow360_volume_mesh_id:  ["flow360 volume mesh id", "f360 volume mesh id"],

  // ── Provenance / timestamps ──────────────────────────────────────────
  created_at: ["created", "created at", "creation date", "when created", "when was this run",
               "when did this run", "run date", "date", "timestamp", "time"],
  updated_at: ["updated", "updated at", "last updated", "last modified", "modified",
               "when updated", "modification date"],
  created_by: ["created by", "who created", "who ran", "ran", "author", "user", "owner",
               "submitted by", "run by", "analyst", "engineer", "who ran this",
               "who submitted", "who started"],
  owned_by:   ["owned by", "owner", "responsible", "assigned to"],

  // ── Provenance metadata ──────────────────────────────────────────────
  source:     ["source", "data source", "origin", "where from", "provenance source",
               "script", "python script", "source script", "source file", "which script",
               "what script", "file path", "notebook"],
  code_ref:   ["code ref", "code reference", "source code", "function", "which function",
               "what function", "code location", "line number", "where in code"],
  vcs_ref:    ["vcs ref", "git ref", "commit", "git commit", "version control",
               "repo version", "git hash", "sha"],

  // ── Staleness ────────────────────────────────────────────────────────
  _is_stale:     ["stale", "is stale", "outdated", "out of date", "expired", "invalid"],
  _stale_reason: ["stale reason", "why stale", "staleness reason", "invalidation reason"],
};

// Computed fields that derive values from multiple row keys
type ComputedField = {
  aliases: string[];
  compute: (row: Record<string, unknown>) => { key: string; value: unknown } | null;
};

const COMPUTED_FIELDS: ComputedField[] = [
  {
    aliases: ["l/d", "ld", "lift to drag", "lift to drag ratio", "lift over drag",
              "aerodynamic efficiency", "l over d", "cl/cd", "cl over cd", "glide ratio"],
    compute: (row) => {
      const cl = row.CL;
      const cd = row.CD;
      if (typeof cl === "number" && typeof cd === "number" && cd !== 0) {
        return { key: "L/D", value: cl / cd };
      }
      return null;
    },
  },
  {
    aliases: ["drag count", "drag counts"],
    compute: (row) => {
      const cd = row.CD;
      if (typeof cd === "number") return { key: "Drag counts", value: cd * 10000 };
      return null;
    },
  },
  {
    aliases: ["oswald", "oswald efficiency", "span efficiency", "e factor"],
    compute: (row) => {
      const cl = row.CL;
      const cd = row.CD;
      const ar = row.aspect_ratio ?? row.AR;
      if (typeof cl === "number" && typeof cd === "number" && typeof ar === "number" && cd !== 0) {
        const e = (cl * cl) / (Math.PI * ar * cd);
        return { key: "Oswald efficiency", value: e };
      }
      return null;
    },
  },
];

const STOP_WORDS = new Set([
  "what", "is", "the", "was", "this", "at", "of", "for", "a", "an",
  "its", "it", "my", "that", "does", "do", "did", "point", "value",
  "tell", "me", "show", "give", "get", "find", "look", "up", "about",
  "can", "you", "please", "whats", "data", "result", "output", "input",
  "used", "set", "to", "in", "with",
]);

const UNITS: Record<string, string> = {
  alpha: "deg", beta: "deg",
  temperature: "K", pressure: "Pa",
  wing_area: "m\u00B2", ref_chord: "m", wing_span: "m",
  ref_area_f360: "m\u00B2", ref_chord_f360: "m",
  created_at: "", updated_at: "",
};

function normalizeQuery(q: string): string {
  return q
    .toLowerCase()
    .replace(/[?!.,;:'"()[\]{}]/g, "")
    .split(/\s+/)
    .filter((w) => !STOP_WORDS.has(w))
    .join(" ")
    .trim();
}

interface FieldResult {
  key: string;
  value: unknown;
  available: boolean;
}

function aliasMatches(norm: string, alias: string): boolean {
  return norm === alias || norm.includes(alias) || alias.includes(norm);
}

function findField(
  query: string,
  row: Record<string, unknown>,
): FieldResult | null {
  const norm = normalizeQuery(query);
  if (!norm) return null;

  // 0. Computed fields first — they answer questions no single key can
  for (const cf of COMPUTED_FIELDS) {
    for (const alias of cf.aliases) {
      if (aliasMatches(norm, alias)) {
        const result = cf.compute(row);
        if (result) return { ...result, available: true };
      }
    }
  }

  // 1. Alias match — check present fields first, then missing ones
  let bestPresent: { key: string; alias: string } | null = null;
  let bestMissing: { key: string; alias: string } | null = null;
  for (const [key, aliases] of Object.entries(FIELD_ALIASES)) {
    const present = row[key] != null;
    for (const alias of aliases) {
      if (!aliasMatches(norm, alias)) continue;
      if (present) {
        if (!bestPresent || alias.length > bestPresent.alias.length)
          bestPresent = { key, alias };
      } else {
        if (!bestMissing || alias.length > bestMissing.alias.length)
          bestMissing = { key, alias };
      }
    }
  }
  if (bestPresent) return { key: bestPresent.key, value: row[bestPresent.key], available: true };

  // 2. Substring match against row keys directly
  const rowKeys = Object.keys(row).filter((k) => !k.startsWith("_") && row[k] != null);
  for (const rk of rowKeys) {
    const lower = rk.toLowerCase().replace(/_/g, " ");
    if (lower.includes(norm) || norm.includes(lower)) {
      return { key: rk, value: row[rk], available: true };
    }
  }

  // 3. Token overlap scoring across both row keys and aliases
  const tokens = norm.split(" ").filter(Boolean);
  let bestKey: string | null = null;
  let bestScore = 0;
  for (const rk of rowKeys) {
    const lower = rk.toLowerCase().replace(/_/g, " ");
    let score = 0;
    for (const t of tokens) if (lower.includes(t)) score++;
    if (score > bestScore) { bestScore = score; bestKey = rk; }
  }
  for (const [key, aliases] of Object.entries(FIELD_ALIASES)) {
    if (row[key] == null) continue;
    for (const alias of aliases) {
      let score = 0;
      for (const t of tokens) if (alias.includes(t)) score++;
      if (score > bestScore) { bestScore = score; bestKey = key; }
    }
  }
  for (const cf of COMPUTED_FIELDS) {
    for (const alias of cf.aliases) {
      let score = 0;
      for (const t of tokens) if (alias.includes(t)) score++;
      if (score > bestScore) {
        const result = cf.compute(row);
        if (result) return { ...result, available: true };
      }
    }
  }
  // Require at least 2 tokens to match — a single token hit (e.g. "sim" inside
  // "simulation name") is too loose and causes misleading results.
  if (bestKey && bestScore >= 2) return { key: bestKey, value: row[bestKey], available: true };

  // 4. We recognized the field via aliases but it's not in this row
  if (bestMissing) return { key: bestMissing.key, value: null, available: false };

  return null;
}

function formatValue(key: string, value: unknown): string {
  if (value == null) return "\u2014";
  if (typeof value === "number") {
    const unit = UNITS[key] ?? "";
    let formatted: string;
    if (Math.abs(value) < 0.001 && value !== 0) formatted = value.toExponential(4);
    else if (Math.abs(value) > 1e6) formatted = value.toExponential(4);
    else if (Number.isInteger(value)) formatted = value.toLocaleString();
    else formatted = value.toPrecision(6);
    return unit ? `${formatted} ${unit}` : formatted;
  }
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// Natural-language chips shown proactively in the Ask panel.
// Ordered by how often an aerospace engineer would want to ask them.
const QUESTION_CHIPS: { key: string; question: string; query: string }[] = [
  { key: "created_by",   question: "Who ran this?",          query: "who ran this"         },
  { key: "Mach",         question: "What's the Mach?",        query: "mach number"          },
  { key: "alpha",        question: "Angle of attack?",        query: "angle of attack"      },
  { key: "CL",           question: "What's CL?",              query: "lift coefficient"     },
  { key: "CD",           question: "What's CD?",              query: "drag coefficient"     },
  { key: "_LD",          question: "What's the L/D?",         query: "lift to drag ratio"   },
  { key: "CM_pitching",  question: "Pitching moment?",        query: "pitching moment"      },
  { key: "residual",     question: "Final residual?",         query: "residual"             },
  { key: "iterations",   question: "How many iterations?",    query: "iterations"           },
  { key: "status",       question: "What's the status?",      query: "status"               },
  { key: "vol_mesh_name",question: "What mesh was used?",     query: "volume mesh"          },
  { key: "geom_name",    question: "What geometry?",          query: "geometry"             },
  { key: "created_at",   question: "When was this run?",      query: "when was this run"    },
  { key: "Re",           question: "Reynolds number?",        query: "reynolds number"      },
  { key: "sweep_group",  question: "Which sweep?",            query: "sweep group"          },
];

function suggestQuestions(row: Record<string, unknown>): { question: string; query: string }[] {
  const chips: { question: string; query: string }[] = [];
  const hasLD = typeof row.CL === "number" && typeof row.CD === "number" && row.CD !== 0;

  for (const chip of QUESTION_CHIPS) {
    if (chips.length >= 4) break;
    if (chip.key === "_LD") {
      if (hasLD) chips.push({ question: chip.question, query: chip.query });
    } else if (chip.key === "created_by") {
      // Always suggest — provenance is always interesting even if not in the flat row
      chips.push({ question: chip.question, query: chip.query });
    } else if (row[chip.key] != null) {
      chips.push({ question: chip.question, query: chip.query });
    }
  }
  return chips;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDataPointContextMenu(chartData?: Record<string, unknown>[]) {
  const [menu, setMenu] = useState<MenuPosition | null>(null);
  const hoveredRef = useRef<PointIdentity | null>(null);
  const chartDataRef = useRef<Record<string, unknown>[]>([]);
  chartDataRef.current = chartData ?? [];

  const setHoveredPoint = useCallback((nodeId: string, row: Record<string, unknown>) => {
    hoveredRef.current = { nodeId, row };
  }, []);

  const clearHoveredPoint = useCallback(() => {
    hoveredRef.current = null;
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const pt = hoveredRef.current;
    if (pt) {
      setMenu({ x: e.clientX, y: e.clientY, kind: "point", point: pt });
    } else if (chartDataRef.current.length) {
      setMenu({ x: e.clientX, y: e.clientY, kind: "plot", chartData: chartDataRef.current });
    }
  }, []);

  const close = useCallback(() => setMenu(null), []);

  return {
    menu,
    close,
    setHoveredPoint,
    clearHoveredPoint,
    handleContextMenu,
  };
}

// ---------------------------------------------------------------------------
// Menu item icons (inline SVG, no extra deps)
// ---------------------------------------------------------------------------

const ICON_SIZE = 14;

function LineageIcon() {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="3" r="2" />
      <circle cx="4" cy="13" r="2" />
      <circle cx="12" cy="13" r="2" />
      <path d="M8 5v3M8 8l-4 3M8 8l4 3" />
    </svg>
  );
}

function SolverIcon() {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="12" height="12" rx="2" />
      <path d="M5 6h6M5 8h4M5 10h5" />
    </svg>
  );
}

function MeshIcon() {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 2l6 3 6-3M2 2v6l6 3M14 2v6l-6 3M8 5v6" />
    </svg>
  );
}

function FlowfieldIcon() {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 8c2-4 4-4 6 0s4 4 6 0" />
      <path d="M2 5c2-3 4-3 6 0s4 3 6 0" opacity="0.5" />
      <path d="M2 11c2-3 4-3 6 0s4 3 6 0" opacity="0.5" />
    </svg>
  );
}

function RefConstantsIcon() {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 3h8M4 8h8M4 13h5" />
      <circle cx="12" cy="13" r="2" />
    </svg>
  );
}

function AskIcon() {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <path d="M6 6.5a2 2 0 0 1 3.5 1.5c0 1-1.5 1.5-1.5 1.5" />
      <circle cx="8" cy="12" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Menu component (portal to body)
// ---------------------------------------------------------------------------

const MENU_ITEMS = [
  { key: "lineage", label: "Show full lineage", Icon: LineageIcon, panel: "dag" as const },
  { key: "solver", label: "Show solver settings", Icon: SolverIcon, panel: "detail" as const },
  { key: "mesh", label: "Show mesh", Icon: MeshIcon, panel: "dag" as const, useMeshNode: true },
  { key: "flowfield", label: "Show flowfield", Icon: FlowfieldIcon, panel: "viewer" as const },
  { key: "refconst", label: "View reference constants", Icon: RefConstantsIcon, panel: "config" as const, configTab: "reference-quantities" as const },
];

// ---------------------------------------------------------------------------
// Plot summary — aggregates provenance across all chart data rows
// ---------------------------------------------------------------------------

export interface PlotSummary {
  count: number;
  authors: string[];
  dateRange: { earliest: string; latest: string } | null;
  sources: string[];
  geometries: string[];
  ranges: { field: string; min: number; max: number; unit?: string }[];
}

export function summarizePlot(rows: Record<string, unknown>[]): PlotSummary {
  const authors = new Set<string>();
  const sources = new Set<string>();
  const geometries = new Set<string>();
  const dates: number[] = [];

  const rangeFields: { key: string; label: string; unit?: string }[] = [
    { key: "Mach", label: "Mach" },
    { key: "alpha", label: "Alpha", unit: "deg" },
    { key: "Re", label: "Re" },
    { key: "CL", label: "CL" },
    { key: "CD", label: "CD" },
  ];
  const mins = new Map<string, number>();
  const maxs = new Map<string, number>();

  for (const row of rows) {
    const author = row.created_by;
    if (typeof author === "string" && author) authors.add(author);

    const ts = row.created_at;
    if (typeof ts === "string") {
      const d = new Date(ts);
      if (!isNaN(d.getTime())) dates.push(d.getTime());
    }

    const src = row.source;
    if (typeof src === "string" && src) {
      sources.add(src.replace(/^file:\/\//, "").split("/").pop() ?? src);
    }
    const codeRef = row.code_ref;
    if (codeRef && typeof codeRef === "object" && !Array.isArray(codeRef)) {
      const file = (codeRef as Record<string, unknown>).file;
      if (typeof file === "string") sources.add(file.split("/").pop() ?? file);
    }

    const geom = row.geom_name;
    if (typeof geom === "string" && geom) geometries.add(geom);

    for (const rf of rangeFields) {
      const v = row[rf.key];
      if (typeof v === "number") {
        const cur_min = mins.get(rf.key);
        const cur_max = maxs.get(rf.key);
        mins.set(rf.key, cur_min === undefined ? v : Math.min(cur_min, v));
        maxs.set(rf.key, cur_max === undefined ? v : Math.max(cur_max, v));
      }
    }
  }

  let dateRange: PlotSummary["dateRange"] = null;
  if (dates.length) {
    dates.sort((a, b) => a - b);
    const fmt = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    dateRange = { earliest: fmt(dates[0]!), latest: fmt(dates[dates.length - 1]!) };
  }

  const ranges: PlotSummary["ranges"] = [];
  for (const rf of rangeFields) {
    const lo = mins.get(rf.key);
    const hi = maxs.get(rf.key);
    if (lo !== undefined && hi !== undefined) {
      ranges.push({ field: rf.label, min: lo, max: hi, unit: rf.unit });
    }
  }

  return {
    count: rows.length,
    authors: [...authors],
    dateRange,
    sources: [...sources],
    geometries: [...geometries],
    ranges,
  };
}

export function fmtRange(r: { field: string; min: number; max: number; unit?: string }): string {
  const fmt = (v: number) => {
    if (Math.abs(v) > 1e6 || (Math.abs(v) < 0.001 && v !== 0)) return v.toExponential(2);
    if (Number.isInteger(v)) return v.toString();
    return v.toPrecision(4);
  };
  const suffix = r.unit ? ` ${r.unit}` : "";
  if (r.min === r.max) return `${r.field}: ${fmt(r.min)}${suffix}`;
  return `${r.field}: ${fmt(r.min)}${suffix} \u2013 ${fmt(r.max)}${suffix}`;
}

// ---------------------------------------------------------------------------
// Component types
// ---------------------------------------------------------------------------

interface ContextMenuProps {
  menu: MenuPosition;
  onClose: () => void;
}

export function DataPointContextMenu({ menu, onClose }: ContextMenuProps): ReactNode {
  if (menu.kind === "plot") {
    return <PlotSummaryCard menu={menu} onClose={onClose} />;
  }
  return <PointContextMenu menu={menu} onClose={onClose} />;
}

function PlotSummaryCard({ menu, onClose }: { menu: PlotMenuPosition; onClose: () => void }): ReactNode {
  const { theme } = useTheme();
  const light = theme === "light";
  const menuRef = useRef<HTMLDivElement>(null);
  const summary = useMemo(() => summarizePlot(menu.chartData), [menu.chartData]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [onClose]);

  const bg = light ? "#ffffff" : "#1c1f1d";
  const textColor = light ? "#111813" : "#e8ebe9";
  const mutedColor = light ? "#6b7570" : "#8b918d";
  const borderColor = light ? "#d5d9d6" : "#2e3330";
  const shadow = light
    ? "0 8px 30px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)"
    : "0 8px 30px rgba(0,0,0,0.5)";

  const menuW = 320;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const x = menu.x + menuW > vw ? vw - menuW - 8 : menu.x;
  const y = menu.y + 300 > vh ? vh - 308 : menu.y;

  const lines: string[] = [];
  lines.push(`${summary.count} data point${summary.count !== 1 ? "s" : ""}.`);
  if (summary.authors.length)
    lines.push(`By ${summary.authors.join(", ")}.`);
  if (summary.dateRange) {
    if (summary.dateRange.earliest === summary.dateRange.latest)
      lines.push(`Run on ${summary.dateRange.earliest}.`);
    else
      lines.push(`Run between ${summary.dateRange.earliest} and ${summary.dateRange.latest}.`);
  }
  if (summary.sources.length)
    lines.push(`Source: ${summary.sources.join(", ")}.`);
  if (summary.geometries.length)
    lines.push(`Geometry: ${summary.geometries.join(", ")}.`);
  if (summary.ranges.length)
    lines.push(summary.ranges.map(fmtRange).join("  \u00B7  "));

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        left: x,
        top: y,
        zIndex: 99999,
        minWidth: menuW,
        maxWidth: 400,
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        boxShadow: shadow,
        padding: "4px 0",
        fontFamily: "var(--fc-font-family, inherit)",
        fontSize: 12,
        color: textColor,
      }}
    >
      <div style={{
        padding: "4px 12px 6px",
        fontSize: 10,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: mutedColor,
        borderBottom: `1px solid ${borderColor}`,
        marginBottom: 4,
      }}>
        Plot Summary
      </div>
      <div style={{ padding: "4px 12px 8px", lineHeight: 1.7 }}>
        {lines.map((line, i) => (
          <div key={i} style={{ color: i === 0 ? textColor : mutedColor, fontSize: i === 0 ? 12 : 11 }}>
            {line}
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}

function PointContextMenu({ menu, onClose }: { menu: PointMenuPosition; onClose: () => void }): ReactNode {
  const { selectArtifactAndOpenPanel } = useProvenanceContext();
  const { theme } = useTheme();
  const light = theme === "light";
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<"menu" | "ask">("menu");
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState<FieldResult | null | undefined>(undefined);

  useEffect(() => {
    setMode("menu");
    setQuery("");
    setAnswer(undefined);
  }, [menu]);

  useEffect(() => {
    if (mode === "ask") inputRef.current?.focus();
  }, [mode]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (mode === "ask") {
          setMode("menu");
          setQuery("");
          setAnswer(undefined);
        } else {
          onClose();
        }
      }
    };
    const scrollHandler = () => onClose();
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    window.addEventListener("scroll", scrollHandler, true);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
      window.removeEventListener("scroll", scrollHandler, true);
    };
  }, [onClose, mode]);

  const handleAction = useCallback(
    (item: typeof MENU_ITEMS[number]) => {
      const nodeId = item.useMeshNode
        ? (menu.point.row._geometry_node_id as string) || menu.point.nodeId
        : menu.point.nodeId;
      const extra = item.configTab ? { configTab: item.configTab } : undefined;
      selectArtifactAndOpenPanel(nodeId, item.panel, extra);
      onClose();
    },
    [menu, selectArtifactAndOpenPanel, onClose],
  );

  const handleAskSubmit = useCallback((overrideQuery?: string) => {
    const q = overrideQuery ?? query;
    if (!q.trim()) return;
    if (overrideQuery !== undefined) setQuery(overrideQuery);
    const result = findField(q, menu.point.row);
    setAnswer(result);
  }, [query, menu]);

  const chips = useMemo(() => suggestQuestions(menu.point.row), [menu.point.row]);
  const menuW = mode === "ask" ? 280 : 220;
  const menuItemsH = (MENU_ITEMS.length + 1) * 34 + 16;
  // chips row: ~30px; shown when no answer yet or after failed search
  const showChips = answer === undefined || answer === null;
  const askH = answer != null && answer.available ? 140 : (showChips ? 118 : 90);
  const menuH = mode === "ask" ? askH : menuItemsH;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const x = menu.x + menuW > vw ? vw - menuW - 8 : menu.x;
  const y = menu.y + menuH > vh ? vh - menuH - 8 : menu.y;

  const bg = light ? "#ffffff" : "#1c1f1d";
  const textColor = light ? "#111813" : "#e8ebe9";
  const mutedColor = light ? "#6b7570" : "#8b918d";
  const borderColor = light ? "#d5d9d6" : "#2e3330";
  const hoverBg = light ? "rgba(0, 100, 60, 0.08)" : "rgba(109, 181, 221, 0.12)";
  const inputBg = light ? "#f5f7f6" : "rgba(255,255,255,0.06)";
  const inputBorder = light ? "#c5cac7" : "#3e4340";
  const resultBg = light ? "rgba(0, 100, 60, 0.06)" : "rgba(109, 181, 221, 0.08)";
  const shadow = light
    ? "0 8px 30px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)"
    : "0 8px 30px rgba(0,0,0,0.5)";

  const menuButtonStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "7px 12px",
    border: "none",
    background: "transparent",
    color: textColor,
    fontSize: 12,
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "left",
    borderRadius: 4,
    margin: "0 4px",
    boxSizing: "border-box",
    maxWidth: "calc(100% - 8px)",
  };

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        left: x,
        top: y,
        zIndex: 99999,
        minWidth: menuW,
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        boxShadow: shadow,
        padding: "4px 0",
        fontFamily: "var(--fc-font-family, inherit)",
        fontSize: 12,
        color: textColor,
      }}
    >
      <div
        style={{
          padding: "4px 12px 6px",
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: mutedColor,
          borderBottom: `1px solid ${borderColor}`,
          marginBottom: 2,
        }}
      >
        {mode === "ask" ? "Ask" : "Data Point"}
      </div>

      {mode === "menu" ? (
        <>
          {MENU_ITEMS.map((item) => (
            <button
              key={item.key}
              onClick={() => handleAction(item)}
              style={menuButtonStyle}
              onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <item.Icon />
              {item.label}
            </button>
          ))}
          <div style={{ borderTop: `1px solid ${borderColor}`, margin: "2px 0" }} />
          <button
            onClick={() => setMode("ask")}
            style={menuButtonStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <AskIcon />
            Ask about this point
          </button>
        </>
      ) : (
        <div style={{ padding: "6px 10px" }}>
          <form
            onSubmit={(e) => { e.preventDefault(); handleAskSubmit(); }}
            style={{ display: "flex", gap: 4 }}
          >
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setAnswer(undefined); }}
              placeholder={chips[0]?.question ?? "e.g. What's the Mach?"}
              style={{
                flex: 1,
                padding: "5px 8px",
                fontSize: 12,
                fontFamily: "inherit",
                background: inputBg,
                border: `1px solid ${inputBorder}`,
                borderRadius: 4,
                color: textColor,
                outline: "none",
              }}
            />
            <button
              type="submit"
              style={{
                padding: "4px 8px",
                fontSize: 11,
                fontFamily: "inherit",
                background: light ? "rgba(0,100,60,0.1)" : "rgba(109,181,221,0.15)",
                border: `1px solid ${light ? "rgba(0,100,60,0.2)" : "rgba(109,181,221,0.25)"}`,
                borderRadius: 4,
                color: textColor,
                cursor: "pointer",
              }}
            >
              Go
            </button>
          </form>

          {/* Chips — shown before any query and after a failed search */}
          {showChips && chips.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {chips.map((chip) => (
                <button
                  key={chip.question}
                  onClick={() => handleAskSubmit(chip.query)}
                  style={{
                    padding: "2px 8px",
                    fontSize: 10,
                    fontFamily: "inherit",
                    background: light ? "rgba(0,100,60,0.07)" : "rgba(109,181,221,0.1)",
                    border: `1px solid ${light ? "rgba(0,100,60,0.18)" : "rgba(109,181,221,0.2)"}`,
                    borderRadius: 10,
                    color: light ? "#1a6640" : "#7ab8d4",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = light ? "rgba(0,100,60,0.14)" : "rgba(109,181,221,0.2)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = light ? "rgba(0,100,60,0.07)" : "rgba(109,181,221,0.1)"; }}
                >
                  {chip.question}
                </button>
              ))}
            </div>
          )}

          {answer !== undefined && answer !== null && (
            <div style={{ marginTop: 8 }}>
              {answer.available ? (
                <div style={{
                  padding: "6px 8px",
                  background: resultBg,
                  borderRadius: 4,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 8,
                }}>
                  <span style={{ color: mutedColor, fontSize: 11 }}>{answer.key}</span>
                  <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 13, color: textColor }}>
                    {formatValue(answer.key, answer.value)}
                  </span>
                </div>
              ) : (
                <div style={{ color: mutedColor, fontSize: 11, lineHeight: 1.5 }}>
                  <strong style={{ color: textColor }}>{answer.key}</strong> isn't in this
                  dataset — try the lineage or solver settings panels.
                </div>
              )}
            </div>
          )}
          {answer === null && (
            <div style={{ color: mutedColor, fontSize: 11, lineHeight: 1.5, marginTop: 8 }}>
              No match — try one of the suggestions above.
            </div>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// ProvenancePopover — reusable "Where did this come from?" button + dropdown
// ---------------------------------------------------------------------------

export function ProvenancePopover({
  data,
  theme,
  label = "Provenance",
  style,
}: {
  data: Record<string, unknown>[];
  theme: string;
  label?: string;
  style?: React.CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const summary = useMemo(() => summarizePlot(data), [data]);
  const light = theme === "light";

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    const keyHandler = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [open]);

  const bg = light ? "#ffffff" : "#1c1f1d";
  const textColor = light ? "#111813" : "#e8ebe9";
  const mutedColor = light ? "#6b7570" : "#8b918d";
  const borderColor = light ? "#d5d9d6" : "#2e3330";
  const shadow = light
    ? "0 8px 24px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)"
    : "0 8px 24px rgba(0,0,0,0.5)";

  const lines: string[] = [];
  lines.push(`${summary.count} data point${summary.count !== 1 ? "s" : ""}.`);
  if (summary.authors.length) lines.push(`By ${summary.authors.join(", ")}.`);
  if (summary.dateRange) {
    lines.push(
      summary.dateRange.earliest === summary.dateRange.latest
        ? `Run on ${summary.dateRange.earliest}.`
        : `Run between ${summary.dateRange.earliest} and ${summary.dateRange.latest}.`,
    );
  }
  if (summary.sources.length) lines.push(`Source: ${summary.sources.join(", ")}.`);
  if (summary.geometries.length) lines.push(`Geometry: ${summary.geometries.join(", ")}.`);
  if (summary.ranges.length) lines.push(summary.ranges.map(fmtRange).join("  \u00B7  "));

  return (
    <div ref={popRef} style={{ position: "relative", ...style }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 10px",
          fontSize: 10,
          fontWeight: 600,
          fontFamily: "var(--fc-font-family, inherit)",
          color: mutedColor,
          background: bg,
          border: `1px solid ${borderColor}`,
          borderRadius: 6,
          cursor: "pointer",
          opacity: 0.85,
          transition: "opacity 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.85"; }}
      >
        <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="8" cy="8" r="6" />
          <path d="M8 5v4M8 11h.01" />
        </svg>
        {label}
      </button>

      {open && (
        <div style={{
          position: "absolute",
          top: "100%",
          right: 0,
          marginTop: 4,
          zIndex: 100,
          minWidth: 300,
          maxWidth: 400,
          background: bg,
          border: `1px solid ${borderColor}`,
          borderRadius: 8,
          boxShadow: shadow,
          fontFamily: "var(--fc-font-family, inherit)",
          fontSize: 12,
          color: textColor,
        }}>
          <div style={{
            padding: "6px 12px",
            fontSize: 10,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: mutedColor,
            borderBottom: `1px solid ${borderColor}`,
          }}>
            Where did this come from?
          </div>
          <div style={{ padding: "8px 12px", lineHeight: 1.7 }}>
            {lines.map((line, i) => (
              <div key={i} style={{ color: i === 0 ? textColor : mutedColor, fontSize: i === 0 ? 12 : 11 }}>
                {line}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
